import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { LoadingArea } from '@consigli/facade';
import {
  useFindings,
  useGetBlobQuery,
  usePackageByTypeAndPackageId,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import {
  DocumentPackageType,
  DocumentType,
  FindingStatus,
  ScheduleCheckFileType,
  ScheduleCheckType,
  convertBlobToDocument,
  toIntList,
} from '@consigli/types';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCheckCircle } from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { IoWarningOutline } from 'react-icons/io5';
import { MdCancel } from 'react-icons/md';

import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { createRenderers } from '@/features/document-viewer/renderers';
import { ScheduleDoubleViewer } from '@/features/document-viewer/schedule-double-viewer';
import { ScheduleSingleViewer } from '@/features/document-viewer/schedule-single-viewer';
import { useFullscreenViewerHost } from '@/features/document-viewer/use-viewer-context';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { useDemoMode } from '@/util/demo-mode';

import { ComparisonStats } from './components/comparison-stats';
import { ComponentReference, ScheduleComponent } from './components/component-card';
import { ComponentSection } from './components/component-section';
import { ScheduleCheckExportButton } from './components/schedule-check-export-button';

const PAGE_SIZE = 50;

// Prev/next navigation is confined to a single section (Complete / Missing in
// some / Missing in both). The flat navigable list is grouped by section, so a
// neighbor in the same section means navigation is allowed; a section change
// (or list edge) means the button greys out instead of jumping across sections.
const isSameSectionNeighbor = (
  navigables: ReadonlyArray<{ section: ScheduleCheckType }>,
  index: number,
  delta: number,
): boolean => {
  const current = navigables[index];
  const neighbor = navigables[index + delta];
  return Boolean(current && neighbor && neighbor.section === current.section);
};

export const ScheduleCheckComparisonPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { adminFeaturesEnabled } = useDemoMode();
  const [missingPage, setMissingPage] = useState(1);
  const [partialPage, setPartialPage] = useState(1);
  const [completePage, setCompletePage] = useState(1);
  const [selectedBlobId, setSelectedBlobId] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [previewDocument, setPreviewDocument] = useState<ReturnType<
    typeof convertBlobToDocument
  > | null>(null);
  const renderers = useMemo(() => createRenderers(), []);

  // Fetch blob data when a blob is selected
  const { data: selectedBlob, isFetching: isFetchingBlob } = useGetBlobQuery(
    {
      projectId,
      packageId,
      blobId: selectedBlobId!,
    },
    { skip: !selectedBlobId },
  );

  // Get package to check if it's published
  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    DocumentPackageType.SCHEDULE_CHECK,
    packageId,
  );

  const isPublished = pkg ? pkg.published : false;

  // Customers wait for the package to be published; admins see the comparison
  // before that, as they do on every other service's results page.
  const waitingForAnalysis = useMemo(() => {
    if (adminFeaturesEnabled) {
      return false;
    }
    return !isPublished;
  }, [isPublished, adminFeaturesEnabled]);

  // Fetch missing findings (no documents uploaded)
  const {
    findings: missingFindings,
    paginatedCount: missingCount,
    isLoading: isLoadingMissing,
  } = useFindings({
    projectId,
    packageId,
    page: missingPage,
    pageSize: PAGE_SIZE,
    status: FindingStatus.ACTIVE,
    scheduleCheckType: ScheduleCheckType.MISSING,
  });

  // Fetch partial findings (missing in some documents)
  const {
    findings: partialFindings,
    paginatedCount: partialCount,
    isLoading: isLoadingPartial,
  } = useFindings({
    projectId,
    packageId,
    page: partialPage,
    pageSize: PAGE_SIZE,
    status: FindingStatus.ACTIVE,
    scheduleCheckType: ScheduleCheckType.PARTIAL,
  });

  // Fetch complete findings (found in all documents)
  const {
    findings: completeFindings,
    paginatedCount: completeCount,
    isLoading: isLoadingComplete,
  } = useFindings({
    projectId,
    packageId,
    page: completePage,
    pageSize: PAGE_SIZE,
    status: FindingStatus.ACTIVE,
    scheduleCheckType: ScheduleCheckType.COMPLETE,
  });

  const isLoading = isLoadingMissing || isLoadingPartial || isLoadingComplete;

  const parseFileMetadata = useCallback(
    (
      metadataSource: unknown,
      fileType: ScheduleCheckFileType,
      metadataTypeName: string,
    ): ComponentReference => {
      if (!metadataSource) {
        return { type: fileType, available: false };
      }

      try {
        const parsedMetadata =
          typeof metadataSource === 'string' ? JSON.parse(metadataSource) : metadataSource;

        const meta = parsedMetadata as Record<string, unknown>;

        const page = meta.page_number !== undefined ? (meta.page_number as number) + 1 : undefined;
        const coordinates =
          meta.x0 !== undefined &&
          meta.y0 !== undefined &&
          meta.x1 !== undefined &&
          meta.y1 !== undefined
            ? {
                x0: meta.x0 as number,
                y0: meta.y0 as number,
                x1: meta.x1 as number,
                y1: meta.y1 as number,
              }
            : undefined;
        const cellCoordinates =
          meta.sheet_name != null
            ? {
                sheetName: meta.sheet_name as string,
                row: toIntList(meta.row),
                col: toIntList(meta.col),
              }
            : undefined;

        return {
          type: fileType,
          available: true,
          page,
          blobId: meta.blob_id as string | undefined,
          fileName: meta.file_name as string | undefined,
          content: meta.content as string | undefined,
          coordinates,
          cellCoordinates,
        };
      } catch (e) {
        console.error(`Failed to parse ${metadataTypeName}:`, e);
        return { type: fileType, available: false };
      }
    },
    [],
  );

  // Transform findings into component data
  const transformFindingsToComponents = useCallback(
    (findings: typeof partialFindings, status: ScheduleCheckType): ScheduleComponent[] => {
      if (!findings || findings.length === 0) return [];

      return findings.map((finding) => {
        const messageContext = finding.messageContext || {};
        const reference = finding.content || `Component ${finding.id}`;

        let metadata: {
          drawing_metadata?: unknown;
          schematic_metadata?: unknown;
          schedule_metadata?: unknown;
          schematic_status?: boolean;
        } | null = null;

        if (messageContext.CUSTOM_MESSAGE) {
          try {
            metadata =
              typeof messageContext.CUSTOM_MESSAGE === 'string'
                ? JSON.parse(messageContext.CUSTOM_MESSAGE)
                : messageContext.CUSTOM_MESSAGE;
          } catch (e) {
            console.error('Failed to parse CUSTOM_MESSAGE:', e);
          }
        }

        const component: ScheduleComponent = {
          id: reference,
          name: reference,
          status: status, // Use the status from the backend filter
          drawings: parseFileMetadata(
            metadata?.drawing_metadata,
            ScheduleCheckFileType.DRAWINGS,
            'drawing_metadata',
          ),
        };

        const schematicStatus = metadata?.schematic_status ?? true;
        if (schematicStatus) {
          component.schematic = parseFileMetadata(
            metadata?.schematic_metadata,
            ScheduleCheckFileType.SCHEMATIC_DRAWINGS,
            'schematic_metadata',
          );
        }

        if (metadata?.schedule_metadata) {
          component.schedule = parseFileMetadata(
            metadata.schedule_metadata,
            ScheduleCheckFileType.SCHEDULE_FILE,
            'schedule_metadata',
          );
        }

        return component;
      });
    },
    [parseFileMetadata],
  );

  const missingComponents = useMemo(
    () => transformFindingsToComponents(missingFindings, ScheduleCheckType.MISSING),
    [missingFindings, transformFindingsToComponents],
  );

  const partialComponents = useMemo(
    () => transformFindingsToComponents(partialFindings, ScheduleCheckType.PARTIAL),
    [partialFindings, transformFindingsToComponents],
  );

  const completeComponents = useMemo(
    () => transformFindingsToComponents(completeFindings, ScheduleCheckType.COMPLETE),
    [completeFindings, transformFindingsToComponents],
  );

  const stats = useMemo(() => {
    return {
      total: missingCount + partialCount + completeCount,
      missing: missingCount,
      partial: partialCount,
      complete: completeCount,
    };
  }, [missingCount, partialCount, completeCount]);

  const [selectedCoordinates, setSelectedCoordinates] = useState<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null>(null);
  const [selectedContent, setSelectedContent] = useState<string | null>(null);

  const [scheduleViewerData, setScheduleViewerData] = useState<{
    clicked: ComponentReference;
    schedule: ComponentReference;
    component: ScheduleComponent;
  } | null>(null);
  const [currentScheduleIndex, setCurrentScheduleIndex] = useState(-1);

  const [scheduleOnlyViewerData, setScheduleOnlyViewerData] = useState<{
    schedule: ComponentReference;
    component: ScheduleComponent;
  } | null>(null);

  // Matches the render condition below: a schedule viewer takes precedence
  // over the document viewer on this screen.
  useFullscreenViewerHost(!scheduleViewerData && !scheduleOnlyViewerData);
  const [currentScheduleOnlyIndex, setCurrentScheduleOnlyIndex] = useState(-1);

  // Flat list of all navigable schedule findings (for prev/next). Each entry is
  // tagged with its section so navigation can be confined within a section.
  const scheduleNavigables = useMemo(() => {
    const all = [...completeComponents, ...partialComponents, ...missingComponents];
    const result: Array<{
      clicked: ComponentReference;
      schedule: ComponentReference;
      component: ScheduleComponent;
      section: ScheduleCheckType;
    }> = [];
    for (const comp of all) {
      if (comp.schedule?.blobId) {
        if (comp.drawings?.blobId)
          result.push({
            clicked: comp.drawings,
            schedule: comp.schedule,
            component: comp,
            section: comp.status,
          });
        if (comp.schematic?.blobId)
          result.push({
            clicked: comp.schematic,
            schedule: comp.schedule,
            component: comp,
            section: comp.status,
          });
      }
    }
    return result;
  }, [completeComponents, partialComponents, missingComponents]);

  // Components where neither drawings nor schematic have a clickable page,
  // but a schedule reference exists — the only way to reach these is via the
  // schedule-only fallback, so they get their own prev/next sequence.
  const scheduleOnlyNavigables = useMemo(() => {
    const all = [...completeComponents, ...partialComponents, ...missingComponents];
    return all
      .filter((comp) => {
        const hasClickableDrawings = Boolean(comp.drawings?.page && comp.drawings?.blobId);
        const hasClickableSchematic = Boolean(comp.schematic?.page && comp.schematic?.blobId);
        return !hasClickableDrawings && !hasClickableSchematic && Boolean(comp.schedule?.blobId);
      })
      .map((comp) => ({
        schedule: comp.schedule as ComponentReference,
        component: comp,
        section: comp.status,
      }));
  }, [completeComponents, partialComponents, missingComponents]);

  // The component whose finding is currently being viewed, derived from whichever
  // viewer is open. Reference identity uniquely maps back to the component (all
  // components share the same drawing/schedule blobId, but each reference object
  // is distinct). Its card is highlighted while viewing, like the Findings list.
  const activeComponentId = useMemo(() => {
    if (scheduleViewerData) return scheduleViewerData.component.id;
    if (scheduleOnlyViewerData) return scheduleOnlyViewerData.component.id;
    if (previewDocument) {
      const all = [...completeComponents, ...partialComponents, ...missingComponents];
      const page = (previewDocument.pageNumber ?? -2) + 1;
      return (
        all.find(
          (comp) =>
            (comp.drawings?.blobId === previewDocument.id && comp.drawings?.page === page) ||
            (comp.schematic?.blobId === previewDocument.id && comp.schematic?.page === page),
        )?.id ?? null
      );
    }
    return null;
  }, [
    scheduleViewerData,
    scheduleOnlyViewerData,
    previewDocument,
    completeComponents,
    partialComponents,
    missingComponents,
  ]);

  // Card DOM nodes keyed by component id, so navigating findings with prev/next
  // can bring the newly active card into view — the schedule-check analogue of
  // version-compare's row auto-scroll. hasScrolledRef keeps the first scroll
  // instant and animates every subsequent one.
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasScrolledRef = useRef(false);

  const registerCard = useCallback(
    (id: string) => (node: HTMLDivElement | null) => {
      if (node) cardRefs.current.set(id, node);
      else cardRefs.current.delete(id);
    },
    [],
  );

  // Navigation (or a card click) changes which component is active, scroll its card into view.
  // The rAF waits for the highlight re-render to paint so the node is in place first.
  useEffect(() => {
    if (activeComponentId == null) {
      hasScrolledRef.current = false;
      return;
    }
    const smooth = hasScrolledRef.current;
    hasScrolledRef.current = true;
    const frame = requestAnimationFrame(() => {
      cardRefs.current.get(activeComponentId)?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'center',
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeComponentId]);

  // Handle opening the document when blob data is loaded (single-pane fallback)
  useEffect(() => {
    if (selectedBlob && selectedBlobId && selectedPage !== null && !isFetchingBlob) {
      const document = convertBlobToDocument(selectedBlob);
      document.pageNumber = selectedPage - 1;
      document.type = DocumentType.FINDING;

      if (selectedContent) {
        document.content = selectedContent;
      }

      if (selectedCoordinates) {
        document.findingCoordinates = {
          x0: selectedCoordinates.x0,
          y0: selectedCoordinates.y0,
          x1: selectedCoordinates.x1,
          y1: selectedCoordinates.y1,
          pageIndex: selectedPage - 1,
        };
      }

      setPreviewDocument(document);
      setSelectedBlobId(null);
      setSelectedPage(null);
      setSelectedCoordinates(null);
      setSelectedContent(null);
    }
  }, [
    selectedBlob,
    selectedBlobId,
    selectedPage,
    selectedCoordinates,
    selectedContent,
    isFetchingBlob,
  ]);

  const handleScheduleNext = useCallback(() => {
    if (!isSameSectionNeighbor(scheduleNavigables, currentScheduleIndex, 1)) return;
    const next = scheduleNavigables[currentScheduleIndex + 1];
    setCurrentScheduleIndex((i) => i + 1);
    setScheduleViewerData({
      clicked: next.clicked,
      schedule: next.schedule,
      component: next.component,
    });
  }, [currentScheduleIndex, scheduleNavigables]);

  const handleSchedulePrevious = useCallback(() => {
    if (!isSameSectionNeighbor(scheduleNavigables, currentScheduleIndex, -1)) return;
    const prev = scheduleNavigables[currentScheduleIndex - 1];
    setCurrentScheduleIndex((i) => i - 1);
    setScheduleViewerData({
      clicked: prev.clicked,
      schedule: prev.schedule,
      component: prev.component,
    });
  }, [currentScheduleIndex, scheduleNavigables]);

  const handleScheduleOnlyNext = useCallback(() => {
    if (!isSameSectionNeighbor(scheduleOnlyNavigables, currentScheduleOnlyIndex, 1)) return;
    const next = scheduleOnlyNavigables[currentScheduleOnlyIndex + 1];
    setCurrentScheduleOnlyIndex((i) => i + 1);
    setScheduleOnlyViewerData({ schedule: next.schedule, component: next.component });
  }, [currentScheduleOnlyIndex, scheduleOnlyNavigables]);

  const handleScheduleOnlyPrevious = useCallback(() => {
    if (!isSameSectionNeighbor(scheduleOnlyNavigables, currentScheduleOnlyIndex, -1)) return;
    const prev = scheduleOnlyNavigables[currentScheduleOnlyIndex - 1];
    setCurrentScheduleOnlyIndex((i) => i - 1);
    setScheduleOnlyViewerData({ schedule: prev.schedule, component: prev.component });
  }, [currentScheduleOnlyIndex, scheduleOnlyNavigables]);

  const handlePageClick = useCallback(
    (clicked: ComponentReference, schedule?: ComponentReference) => {
      if (schedule?.blobId) {
        // Reset the other viewers so the new selection is not masked behind them.
        setScheduleOnlyViewerData(null);
        setPreviewDocument(null);
        // Match by object identity: in schedule-check the drawing/schedule are
        // single files, so all entries share the same blobId. parseFileMetadata
        // produces a distinct reference per component, so identity is unique.
        const idx = scheduleNavigables.findIndex(
          (n) => n.clicked === clicked && n.schedule === schedule,
        );
        const entry = scheduleNavigables[idx];
        if (!entry) return;
        setCurrentScheduleIndex(idx);
        setScheduleViewerData({ clicked, schedule, component: entry.component });
      } else if (clicked.blobId && clicked.page) {
        // Reset the schedule viewers so the single-pane preview is not masked.
        setScheduleViewerData(null);
        setScheduleOnlyViewerData(null);
        setSelectedBlobId(clicked.blobId);
        setSelectedPage(clicked.page);
        setSelectedCoordinates(clicked.coordinates ?? null);
        setSelectedContent(clicked.content ?? null);
      }
    },
    [scheduleNavigables],
  );

  const handleScheduleOnlyClick = useCallback(
    (schedule: ComponentReference) => {
      // Reset the other viewers so the new selection is not masked behind them.
      setScheduleViewerData(null);
      setPreviewDocument(null);
      const idx = scheduleOnlyNavigables.findIndex((entry) => entry.schedule === schedule);
      const entry = scheduleOnlyNavigables[idx];
      if (!entry) return;
      setCurrentScheduleOnlyIndex(idx);
      setScheduleOnlyViewerData({ schedule, component: entry.component });
    },
    [scheduleOnlyNavigables],
  );

  return (
    <>
      <Breadcrumb label={t('schedule-check-tabs.comparison')} />
      <WorkspaceProvider>
        <WorkspaceLayout hideTabs={waitingForAnalysis}>
          {waitingForAnalysis ? (
            <WaitingForAnalysis />
          ) : isLoading ? (
            <LoadingArea title={t('schedule-check-comparison.loading')} />
          ) : (
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <HiOutlineDocumentText size={32} className="text-gray-700" />
                    <h1 className="text-3xl font-bold text-gray-900">
                      {t('schedule-check-comparison.page-title')}
                    </h1>
                  </div>
                  <ScheduleCheckExportButton />
                </div>
                <p className="text-gray-600">{t('schedule-check-comparison.page-subtitle')}</p>
              </div>

              <ComparisonStats
                totalComponents={stats.total}
                complete={stats.complete}
                missingInSome={stats.partial}
                missingInAll={stats.missing}
              />

              <ComponentSection
                index={0}
                title={t('schedule-check-comparison.sections.complete')}
                icon={<FaRegCheckCircle className="text-green-500" size={20} />}
                badgeColor="bg-gray-100 text-gray-700"
                count={completeCount}
                components={completeComponents}
                currentPage={completePage}
                pageSize={PAGE_SIZE}
                defaultOpen={true}
                onPageChange={setCompletePage}
                onPageClick={handlePageClick}
                onScheduleOnlyClick={handleScheduleOnlyClick}
                activeComponentId={activeComponentId}
                registerCard={registerCard}
              />

              <ComponentSection
                index={1}
                title={t('schedule-check-comparison.sections.missing-in-some')}
                icon={<IoWarningOutline className="text-orange-500" size={20} />}
                badgeColor="bg-gray-100 text-gray-700"
                count={partialCount}
                components={partialComponents}
                currentPage={partialPage}
                pageSize={PAGE_SIZE}
                defaultOpen={true}
                onPageChange={setPartialPage}
                onPageClick={handlePageClick}
                onScheduleOnlyClick={handleScheduleOnlyClick}
                activeComponentId={activeComponentId}
                registerCard={registerCard}
              />

              <ComponentSection
                index={2}
                title={t('schedule-check-comparison.sections.missing-in-both')}
                icon={<MdCancel className="text-red-500" size={20} />}
                badgeColor="bg-red-100 text-red-700"
                count={missingCount}
                components={missingComponents}
                currentPage={missingPage}
                pageSize={PAGE_SIZE}
                defaultOpen={false}
                onPageChange={setMissingPage}
                onPageClick={handlePageClick}
                onScheduleOnlyClick={handleScheduleOnlyClick}
                activeComponentId={activeComponentId}
                registerCard={registerCard}
              />
            </div>
          )}
        </WorkspaceLayout>
        {scheduleViewerData && (
          <ScheduleDoubleViewer
            schedule={scheduleViewerData.schedule}
            clicked={scheduleViewerData.clicked}
            component={scheduleViewerData.component}
            isPreview={true}
            onClose={() => setScheduleViewerData(null)}
            onNext={handleScheduleNext}
            onPrevious={handleSchedulePrevious}
            hasNext={isSameSectionNeighbor(scheduleNavigables, currentScheduleIndex, 1)}
            hasPrevious={isSameSectionNeighbor(scheduleNavigables, currentScheduleIndex, -1)}
          />
        )}
        {!scheduleViewerData && scheduleOnlyViewerData && (
          <ScheduleSingleViewer
            schedule={scheduleOnlyViewerData.schedule}
            component={scheduleOnlyViewerData.component}
            isPreview={true}
            onClose={() => setScheduleOnlyViewerData(null)}
            onNext={handleScheduleOnlyNext}
            onPrevious={handleScheduleOnlyPrevious}
            hasNext={isSameSectionNeighbor(scheduleOnlyNavigables, currentScheduleOnlyIndex, 1)}
            hasPrevious={isSameSectionNeighbor(
              scheduleOnlyNavigables,
              currentScheduleOnlyIndex,
              -1,
            )}
          />
        )}
        {!scheduleViewerData && !scheduleOnlyViewerData && previewDocument && (
          <DocumentViewer
            open
            onOpenChange={(details) => {
              if (!details.open) setPreviewDocument(null);
            }}
            src={previewDocument.fileUrl || previewDocument.downloadUrl || ''}
            fileName={previewDocument.name}
            title={previewDocument.name}
            showSidebar={false}
            renderers={renderers}
          />
        )}
      </WorkspaceProvider>
    </>
  );
};
